import { useState } from "react";
import { motion } from "framer-motion";
import { FaWhatsapp } from "react-icons/fa";
import { Calendar as CalendarIcon, MapPin, Wallet, Home as HomeIcon, Check } from "lucide-react";
import { format } from "date-fns";
import { tr as trLocale, az as azLocale, pl as plLocale } from "date-fns/locale";
const LOCALES = { tr: trLocale, az: azLocale, pl: plLocale };
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { BRAND } from "@/lib/brand";
import { useT } from "@/lib/i18n";

// District names are stable — translatable label only for "Any / Not sure".
const RAW_DISTRICTS = [
  "__any__",
  "Śródmieście",
  "Mokotów",
  "Wola",
  "Ochota",
  "Praga-Południe",
  "Praga-Północ",
  "Żoliborz",
  "Ursynów",
  "Wilanów",
  "Bielany",
  "Bemowo",
  "Targówek",
  "Włochy",
];

const TYPE_KEYS = [
  { value: "room", key: "type.room" },
  { value: "studio", key: "type.studio" },
  { value: "flat-1-2", key: "type.flat-1-2" },
  { value: "flat-3plus", key: "type.flat-3plus" },
  { value: "house", key: "type.house" },
];

export default function RequestViewing() {
  const { t, lang } = useT();
  const [districts, setDistricts] = useState([]);
  const [type, setType] = useState("");
  const [budget, setBudget] = useState("");
  const [moveDate, setMoveDate] = useState(null);
  const [name, setName] = useState("");

  const districtLabel = (d) => (d === "__any__" ? t("district.any") : d);

  const toggleDistrict = (d) => {
    setDistricts((prev) => {
      if (d === "__any__") return prev.includes(d) ? [] : [d];
      const without = prev.filter((x) => x !== "__any__");
      return without.includes(d) ? without.filter((x) => x !== d) : [...without, d];
    });
  };

  const sendToWhatsApp = (e) => {
    e.preventDefault();
    if (districts.length === 0 || !type || !budget) {
      toast.error(t("rv.err"));
      return;
    }
    const typeLabel = t(TYPE_KEYS.find((tt) => tt.value === type)?.key || type);
    const dateLabel = moveDate
      ? format(moveDate, "PPP", { locale: LOCALES[lang] })
      : t("rv.msg.flexible");
    const greeting = name.trim()
      ? t("rv.msg.hi_name", { name: name.trim() })
      : t("rv.msg.hi");
    const districtsValue = districts.map(districtLabel).join(", ");
    const districtLabelText = districts.length > 1 ? t("rv.msg.districts") : t("rv.msg.district");

    const msg = `${greeting}

${t("rv.msg.intro")}

• ${districtLabelText}: ${districtsValue}
• ${t("rv.msg.type")}: ${typeLabel}
• ${t("rv.msg.budget")}: ${budget} PLN / ${{ tr: "ay", az: "ay", pl: "mies." }[lang] || "month"}
• ${t("rv.msg.move_in")}: ${dateLabel}

${t("rv.msg.outro")}`;
    window.open(`https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
    toast.success(t("rv.opening"));
  };

  return (
    <section id="request-viewing" data-testid="request-viewing-section" className="relative py-24 md:py-28 bg-navy-800/30">
      <div className="absolute inset-x-0 top-0 divider-gold" />
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="lg:col-span-5">
            <div className="text-xs tracking-[0.3em] text-gold uppercase mb-4">{t("rv.eyebrow")}</div>
            <h2 className="font-serif text-4xl lg:text-5xl text-white leading-tight mb-5">
              {t("rv.title.1")}{" "}
              <span className="italic text-gold">{t("rv.title.2")}</span>
            </h2>
            <p className="text-slate-400 leading-relaxed">{t("rv.blurb")}</p>
            <ul className="mt-8 space-y-3 text-sm text-slate-300">
              {["rv.benefit.1", "rv.benefit.2", "rv.benefit.3"].map((k) => (
                <li key={k} className="flex items-center gap-3">
                  <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                  {t(k)}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.form
            onSubmit={sendToWhatsApp}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            data-testid="request-viewing-form"
            className="lg:col-span-7 bg-navy-800/70 backdrop-blur-xl border border-gold/25 rounded-3xl p-7 md:p-10 shadow-2xl"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label={t("rv.f.name")}>
                <Input data-testid="rv-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("rv.f.name.ph")} className="h-12 bg-navy-900 border-gold/25 text-white placeholder:text-slate-500 focus-visible:ring-gold focus-visible:border-gold rounded-xl" />
              </Field>

              <Field label={t("rv.f.type")} icon={HomeIcon} required>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger data-testid="rv-type-trigger" className="h-12 bg-navy-900 border-gold/25 text-white data-[placeholder]:text-slate-500 focus:ring-gold rounded-xl">
                    <SelectValue placeholder={t("rv.f.type.ph")} />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-900 border-gold/25 text-white">
                    {TYPE_KEYS.map((tt) => (
                      <SelectItem key={tt.value} value={tt.value} data-testid={`rv-type-${tt.value}`} className="focus:bg-gold/15 focus:text-gold-light cursor-pointer">
                        {t(tt.key)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label={t("rv.f.district")} icon={MapPin} required>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="rv-district-trigger" className="h-12 w-full px-3 rounded-xl bg-navy-900 border border-gold/25 text-left text-sm text-white hover:border-gold/60 transition-colors flex items-center justify-between gap-2">
                      <span className={districts.length === 0 ? "text-slate-500" : "text-white truncate"}>
                        {districts.length === 0
                          ? t("rv.f.district.ph")
                          : districts.length === 1
                          ? districtLabel(districts[0])
                          : `${districts.length} ${t("rv.f.district.summary")}`}
                      </span>
                      <MapPin className="h-4 w-4 text-gold/80 shrink-0" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0 bg-navy-900 border-gold/25 text-white max-h-72 overflow-y-auto">
                    <ul className="py-1">
                      {RAW_DISTRICTS.map((d) => {
                        const checked = districts.includes(d);
                        return (
                          <li key={d}>
                            <button type="button" onClick={() => toggleDistrict(d)} data-testid={`rv-district-option-${d.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={`w-full text-left flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${checked ? "bg-gold/15 text-gold-light" : "text-slate-200 hover:bg-gold/10"}`}>
                              <span className={`h-4 w-4 rounded border flex items-center justify-center transition-colors ${checked ? "bg-gold border-gold text-navy-900" : "border-gold/40"}`}>
                                {checked && <Check className="h-3 w-3" />}
                              </span>
                              {districtLabel(d)}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </PopoverContent>
                </Popover>
                {districts.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="rv-selected-districts">
                    {districts.map((d) => (
                      <button type="button" key={d} onClick={() => toggleDistrict(d)} className="inline-flex items-center gap-1 bg-gold/15 border border-gold/40 text-gold-light text-xs rounded-full px-2.5 py-1 hover:bg-gold/25 transition-colors">
                        {districtLabel(d)}
                        <span className="text-gold">×</span>
                      </button>
                    ))}
                  </div>
                )}
              </Field>

              <Field label={t("rv.f.budget")} icon={Wallet} required>
                <Input data-testid="rv-budget" inputMode="numeric" value={budget} onChange={(e) => setBudget(e.target.value.replace(/[^0-9]/g, ""))} placeholder={t("rv.f.budget.ph")} className="h-12 bg-navy-900 border-gold/25 text-white placeholder:text-slate-500 focus-visible:ring-gold focus-visible:border-gold rounded-xl" />
              </Field>

              <Field label={t("rv.f.date")} icon={CalendarIcon}>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="rv-date-trigger" className="h-12 w-full px-3 rounded-xl bg-navy-900 border border-gold/25 text-left text-sm text-white hover:border-gold/60 transition-colors flex items-center justify-between">
                      <span className={moveDate ? "text-white" : "text-slate-500"}>
                        {moveDate ? format(moveDate, "PPP", { locale: LOCALES[lang] }) : t("rv.f.date.ph")}
                      </span>
                      <CalendarIcon className="h-4 w-4 text-gold/80" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-auto p-0 bg-navy-900 border-gold/25 text-white">
                    <Calendar locale={LOCALES[lang]} mode="single" selected={moveDate} onSelect={setMoveDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus className="rounded-lg" />
                  </PopoverContent>
                </Popover>
              </Field>
            </div>

            <button type="submit" data-testid="rv-submit" className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-gold hover:bg-gold-light text-navy-900 font-semibold px-8 py-4 rounded-full transition-[color,background-color,border-color,box-shadow,transform] shadow-[0_10px_40px_-10px_rgba(201,162,75,0.55)] hover:-translate-y-0.5">
              <FaWhatsapp className="h-5 w-5" />
              {t("rv.submit")}
            </button>
            <p className="mt-4 text-xs text-slate-500 text-center">{t("rv.no_store")}</p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, icon: Icon, required, children }) {
  return (
    <div className="flex flex-col gap-2">
      <Label className="text-xs tracking-wider text-slate-400 uppercase flex items-center gap-2">
        {Icon && <Icon className="h-3.5 w-3.5 text-gold" />}
        {label}
        {required && <span className="text-gold">*</span>}
      </Label>
      {children}
    </div>
  );
}
